import type { FullCandidate } from '@core/candidate-types';
import type { Placement } from '@core/placements-types';

/**
 * Takes an array of FullCandidate objects as input and returns an array
 * of Placement objects that are common to all candidates.
 * It first checks if the array is empty or contains only one candidate,
 * in which case it returns an empty array or the placements of the single
 * candidate, respectively. For multiple candidates,
 * it creates a map of placements for each offer ID,
 * and then returns the placements that are common to all candidates.
 * @param candidates
 */
export function getCommonOfferPlacements(candidates: FullCandidate[]): Placement[] {
  if (candidates.length === 0) {
    return [];
  } else if (candidates.length === 1) {
    return candidates[0].placements;
  }

  const offerPlacements = new Map<number, Placement[]>();
  candidates.forEach(candidate => {
    candidate.placements.forEach(placement => {
      if (offerPlacements.has((placement.offer?.id || placement.talentPool?.id) ?? -1)) {
        offerPlacements.get((placement.offer?.id || placement.talentPool?.id)!)!.push(placement);
      } else {
        offerPlacements.set((placement.offer?.id || placement.talentPool?.id)!, [placement]);
      }
    });
  });

  return Array.from(offerPlacements, ([_, placements]) => placements)
    .filter(placements => placements.length === candidates.length)
    .flat();
}

/**
 * Checks if a candidate's data should be visible based on their anonymity and reveal status.
 *
 * @param candidate - The candidate object with `isRevealed` and `isAnonymous` properties.
 * @returns `true` if the candidate's data should be visible, `false` otherwise.
 *
 * The presence and value of `isRevealed` directly influence the visibility decision,
 * taking precedence over `isAnonymous` when it is defined.
 */
export function isCandidateDataVisible(candidate?: {
  isRevealed?: boolean;
  isAnonymous?: boolean;
}): boolean {
  return !candidate?.isAnonymous || !!candidate?.isRevealed;
}

/**
 * Checks if a candidate has answered screening questions for a single offer only and has a single placement.
 */
export function hasSingleCtxScreening(
  placements: Placement[],
  groupedOpenQuestionAnswers: Array<{ offer: { id: number | null } | null }>,
): boolean {
  const screeningOfferIds = new Set(
    groupedOpenQuestionAnswers.map(group => group.offer?.id).filter(Boolean),
  );

  if (screeningOfferIds.size !== 1 || placements.length !== 1) {
    return false;
  }

  const offerId: number = Array.from(screeningOfferIds.values())[0]!;
  const placementOfferId = placements[0].offerId || placements[0].offer?.id;
  return offerId === placementOfferId;
}
